// src/pages/master/SuperAdminManagement.jsx
import React, { useState, useEffect } from "react";
import API from "../../api";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Snackbar, Alert } from "@mui/material";

export default function SuperAdminManagement() {
  // List of superadmins + commissions
  const [admins, setAdmins] = useState([]);
  const [commissions, setComms] = useState([]);

  // Dialog & form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    branchName: "",
    address: "",
    pincode: "",
    phone: "",
    type: "individual",
    commission: "",
  });

  // Delete confirmation
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });

  // Snackbar
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const show = (msg, sev = "error") =>
    setSnack({ open: true, message: msg, severity: sev });

  // Load both lists
  const loadAll = async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        API.get("/master/superadmins"),
        API.get("/master/commissions"),
      ]);
      setAdmins(aRes.data);
      setComms(cRes.data);
    } catch {
      show("Failed to load data");
    }
  };
  useEffect(() => {
    loadAll();
  }, []);

  // Handlers
  const openAdd = () => {
    setEditing(null);
    setForm({
      name: "",
      email: "",
      password: "",
      branchName: "",
      address: "",
      pincode: "",
      phone: "",
      type: "individual",
      commission: "",
    });
    setDialogOpen(true);
  };
  const openEdit = (sa) => {
    setEditing(sa);
    setForm({
      name: sa.name,
      email: sa.email,
      password: "",
      branchName: sa.branchName || "",
      address: sa.address || "",
      pincode: sa.pincode || "",
      phone: sa.phone || "",
      type: sa.type,
      commission: sa.commission?._id?.toString() || "",
    });
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.commission) {
      show("Please select a commission grade", "warning");
      return;
    }
    try {
      if (editing) {
        await API.put(`/master/superadmins/${editing._id}`, form);
        show("Updated successfully", "success");
      } else {
        await API.post("/master/superadmins", form);
        show("Created successfully", "success");
      }
      closeDialog();
      loadAll();
    } catch (err) {
      show(err.response?.data?.message || "Save failed");
    }
  };

  const askDelete = (sa) =>
    setConfirm({ open: true, id: sa._id, name: sa.name });
  const cancelDelete = () => setConfirm({ open: false, id: null, name: "" });
  const confirmDelete = async () => {
    try {
      await API.delete(`/master/superadmins/${confirm.id}`);
      show("Deleted", "success");
      loadAll();
    } catch {
      show("Delete failed");
    } finally {
      cancelDelete();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Super Admins</h1>

      <button
        onClick={openAdd}
        className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        Add Super Admin
      </button>

      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-gray-700">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Commission
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {admins.map((sa) => (
              <tr key={sa._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sa.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sa.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sa.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {sa.commission?.name || "—"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  <button
                    onClick={() => openEdit(sa)}
                    className="text-indigo-600 hover:text-indigo-900 mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => askDelete(sa)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirm.open}
        title="Confirm Delete"
        content={`Delete SuperAdmin “${confirm.name}”?`}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      {/* Add/Edit Dialog */}
      {dialogOpen && (
        <div
          className="fixed inset-0 bg-white/20 backdrop-blur-lg flex items-start justify-center overflow-auto z-50 mt-8 mb-8"
          onClick={closeDialog}
        >
          <div
            className="
     bg-white/80 backdrop-blur-md rounded-lg shadow-xl
     w-full max-w-md mt-16 mb-16 overflow-auto max-h-screen
     transform transition duration-300 ease-out
     animate-[fade-in_300ms_ease-out_forwards]
   "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4">
              <h2 className="text-lg font-medium text-gray-800">
                {editing ? "Edit Super Admin" : "Add Super Admin"}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {[
                { label: "Name", name: "name", type: "text" },
                { label: "Email", name: "email", type: "email" },
                ...(!editing
                  ? [{ label: "Password", name: "password", type: "password" }]
                  : []),
                { label: "Branch Name", name: "branchName", type: "text" },
                { label: "Address", name: "address", type: "text" },
                { label: "Pincode", name: "pincode", type: "text" },
                { label: "Phone", name: "phone", type: "text" },
              ].map(({ label, name, type }) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    type={type}
                    value={form[name]}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, [name]: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring focus:ring-blue-200"
                  />
                </div>
              ))}

              {/* Type selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring focus:ring-blue-200"
                >
                  <option value="bank">Bank</option>
                  <option value="individual">Individual</option>
                </select>
              </div>

              {/* Commission selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commission Grade
                </label>
                <select
                  value={form.commission}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, commission: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring focus:ring-blue-200"
                >
                  <option value="">Select a grade…</option>
                  {commissions.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} ({c.grade}) – {c.percent}%
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={closeDialog}
                className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
